import React, { useEffect } from "react";
import { HomeOutlined, UserOutlined } from "@ant-design/icons";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Input, Space, Table, Breadcrumb, notification, Modal, Switch } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import { useState, useRef } from "react";
import Highlighter from "react-highlight-words";
import "./data_purchase_order.css";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

interface DataType {
     key: string;
     poNo: string;
     poDate: string;
     orderType: string;
     supplier: string;
     warehouse: string;
     status: string;
     statusCode: string;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then((res) => res.data);
const DataPurchaseOrder: React.FC = () => {
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };

     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const searchInput = useRef<InputRef>(null);
     const { userData } = useAuth();
     const [showAllWarehouse, setShowAllWarehouse] = useState<boolean>(false);
     const hasAuthority77 = userData?.otoritas
          ? userData.otoritas.split(';').map((val: string) => val.trim()).includes('77')
          : false;
     const [dataSource, setDataSource] = useState<DataType[]>([]);
     const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
     const [sortField, setSortField] = useState<string | undefined>(undefined);
     const [sortOrder, setSortOrder] = useState<string | undefined>(undefined);
     const [filters, setFilters] = useState<Record<string, any>>({});
     const [total, setTotal] = useState(0);

     const navigate = useNavigate();
     const queryParams = new URLSearchParams({
          page: pagination.current.toString(),
          pageSize: pagination.pageSize.toString(),
          ...(sortField && { sortField }),
          ...(sortOrder && { sortOrder }),
          ...filters,
          ...(!showAllWarehouse && userData?.warehouse_code && { dealer_rep_code: userData.warehouse_code }),
     }).toString();
     const { data, error, isLoading, mutate } = useSWR<{ data: DataType[]; total: number }>(
          `${process.env.REACT_APP_BACKEND_URL}/api/po/purchase-orders?${queryParams}`,
          fetcher,
     );
     useEffect(() => {
          if (data) {
               setDataSource(data.data);
               setTotal(data.total);
          }
     }, [data]);
     const handleRowDoubleClick = (record: DataType) => {
          navigate(`/dashboard/purchase_order`, { state: { poNo: record.poNo } });
     };

     if (error) {
          notification.error({
               message: "Gagal Memuat Data",
               description: "Tidak dapat mengambil data Purchase Order dari server. Silakan coba lagi nanti.",
          });
     }

     const handleApprove = (poNo: string) => {
          Modal.confirm({
               title: "Konfirmasi Persetujuan",
               content: `Apakah anda yakin untuk Approved PO ${poNo} ini?`,
               okText: "Ya, Approve",
               cancelText: "Batal",
               onOk: async () => {
                    try {
                         const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/po/approve/${poNo}`);
                         notification.success({ message: "Berhasil", description: response.data.message });
                         mutate();
                    } catch (err: any) {
                         notification.error({
                              message: "Gagal Approve",
                              description: err.response?.data?.error || "Terjadi kesalahan pada server.",
                         });
                    }
               },
          });
     };

     const handleRevise = (poNo: string) => {
          Modal.confirm({
               title: "Konfirmasi Revisi",
               content: `Apakah Anda yakin ingin merevisi PO ${poNo}? Status akan dikembalikan ke "Draft".`,
               okText: "Ya, Revisi",
               cancelText: "Batal",
               onOk: async () => {
                    try {
                         const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/po/revise/${poNo}`);
                         notification.success({ message: "Berhasil", description: response.data.message });
                         mutate();
                    } catch (err: any) {
                         notification.error({
                              message: "Gagal Revisi",
                              description: err.response?.data?.error || "Terjadi kesalahan pada server.",
                         });
                    }
               },
          });
     };

     const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps["confirm"], dataIndex: DataIndex) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void, confirm: FilterDropdownProps["confirm"]) => {
          clearFilters();
          setSearchText("");
          confirm();
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${dataIndex}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const columns: TableColumnsType<DataType> = [
          {
               title: "PO No",
               dataIndex: "poNo",
               key: "poNo",
               ...getColumnSearchProps("poNo"),
               sorter: true,
               sortDirections: ["descend", "ascend"],
          },
          {
               title: "PO Date",
               dataIndex: "poDate",
               key: "poDate",
               ...getColumnSearchProps("poDate"),
               sorter: true,
               sortDirections: ["descend", "ascend"],
          },
          {
               title: "Order Type",
               dataIndex: "orderType",
               key: "orderType",
               ...getColumnSearchProps("orderType"),
               sorter: true,
               sortDirections: ["descend", "ascend"],
          },
          {
               title: "Supplier",
               dataIndex: "supplier",
               key: "supplier",
               ...getColumnSearchProps("supplier"),
               sorter: true,
               sortDirections: ["descend", "ascend"],
          },
          {
               title: "Warehouse",
               dataIndex: "warehouse",
               key: "warehouse",
               ...getColumnSearchProps("warehouse"),
               sorter: true,
               sortDirections: ["descend", "ascend"],
          },
          {
               title: "Status",
               dataIndex: "status",
               key: "status",
               ...getColumnSearchProps("status"),
               sorter: true,
               sortDirections: ["descend", "ascend"],
          },
     ];

     const handleTableChange = (paginationParams: any, filtersParams: any, sorterParams: any) => {
          setPagination({ current: paginationParams.current, pageSize: paginationParams.pageSize });
          if (sorterParams.field) {
               setSortField(sorterParams.field);
               setSortOrder(sorterParams.order);
          } else {
               setSortField(undefined);
               setSortOrder(undefined);
          }
          const nextFilters: Record<string, any> = {};
          Object.keys(filtersParams).forEach((key) => {
               if (filtersParams[key] && filtersParams[key].length > 0) {
                    nextFilters[key] = filtersParams[key][0];
               }
          });
          setFilters(nextFilters);
     };

     return (
          <div id="ilqt" style={transformStyle}>
               <div className="gjs-grid-row" id="iadi">
                    <div className="gjs-grid-column" id="i69i">
                         <Breadcrumb
                              items={[
                                   { title: <HomeOutlined /> },
                                   {
                                        title: (
                                             <>
                                                  <span>Data Purchase Order</span>
                                             </>
                                        ),
                                   },
                                   { href: "#/dashboard/purchase_order", title: "Purchase Order" },
                              ]}
                         />
                    </div>
               </div>

               <div className="gjs-grid-row" id="iiodk">
                    <div className="gjs-grid-column" id="33dda">
                         {hasAuthority77 && (
                              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                   <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                                   <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                              </div>
                         )}
                         <Table<DataType>
                              columns={columns}
                              dataSource={dataSource}
                              loading={isLoading}
                              pagination={{
                                   current: pagination.current,
                                   pageSize: pagination.pageSize,
                                   total: total,
                                   showSizeChanger: true,
                              }}
                              onChange={handleTableChange}
                              onRow={(record) => ({ onDoubleClick: () => handleRowDoubleClick(record) })}
                         />
                    </div>
               </div>
          </div>
     );
};

export default DataPurchaseOrder;
